import { sortBy } from 'lodash/fp';
import { Controller } from 'react-hook-form';
import { Link } from 'react-router-dom-v5-compat';
import { FormError } from 'shared/components/FormControl';

import ReactSelect from 'dashboard/components/ReactSelect';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label, Paragraph } from 'design-system/components/Text';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface CasesCreateCaseModalProps {
  caseTemplatesMeta: { id: string; name: string; prefix: string }[];
  submit: (data: CreateCaseFormData) => Promise<void>;
  handleClose: () => void;
}

const CasesCreateCaseModal = ({
  caseTemplatesMeta,
  submit,
  handleClose,
}: CasesCreateCaseModalProps) => {
  return (
    <ModalContainer>
      <ModalHeader onClose={handleClose}>Create a new case</ModalHeader>

      <ModalBody>
        {caseTemplatesMeta.length > 0 ? (
          <FormCreateCase
            caseTemplatesMeta={caseTemplatesMeta}
            submit={submit}
            cancel={handleClose}
          />
        ) : (
          <Paragraph>
            You currently have no active case templates. Please{' '}
            <Link to='/dashboard/case-templates'>create a case template to get started</Link>.
          </Paragraph>
        )}
      </ModalBody>
    </ModalContainer>
  );
};

export interface CreateCaseFormData {
  caseTemplateId: string;
}

interface FormCreateCaseProps {
  caseTemplatesMeta: { id: string; name: string; prefix: string }[];
  submit: (data: CreateCaseFormData) => Promise<void>;
  cancel: () => void;
}

const FormCreateCase = ({ caseTemplatesMeta, submit, cancel }: FormCreateCaseProps) => {
  const defaultValues: CreateCaseFormData = {
    caseTemplateId: caseTemplatesMeta[0].id,
  };
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValid, errors },
  } = useFormSecurely({ defaultValues: defaultValues, mode: 'onChange' });

  const caseTemplateOptions = caseTemplatesMeta.map((caseTemplateMeta) => ({
    label: `${caseTemplateMeta.name} (${caseTemplateMeta.prefix})`,
    value: caseTemplateMeta.id,
  }));

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Label size='md' id='cases-create-case-modal__case-templates--label' weight='bold'>
        Case template
      </Label>
      <Controller
        control={control}
        name='caseTemplateId'
        rules={{ required: 'Please select a case template.' }}
        render={({ field: { onChange, value } }) => (
          <ReactSelect
            id='cases-create-case-modal__case-templates'
            value={value}
            onChange={onChange}
            options={sortBy('label', caseTemplateOptions)}
            isMulti={false}
            portal={true}
            aria-labelledby='cases-create-case-modal__case-templates--label'
          />
        )}
      />
      {errors.caseTemplateId && <FormError>{errors.caseTemplateId.message}</FormError>}
      <Spacer height={4} />
      <HStack equal={true}>
        <Button
          variant='basic'
          id='cases-create-case-modal__cancel'
          autoFocus={true}
          disabled={isSubmitting}
          onClick={cancel}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          variant='primary'
          id='cases-create-case-modal__confirm'
          disabled={isSubmitting || !isValid}
          isPending={isSubmitting}
          aria-label='Create case'
        >
          Create case
        </Button>
      </HStack>
    </form>
  );
};

export default CasesCreateCaseModal;
